
import { supabase } from "@/integrations/supabase/client";
import { Subscription } from "./types";
import { toast } from "@/hooks/use-toast";

// Get current user's subscription
export const getCurrentSubscription = async (): Promise<Subscription | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;

  try {
    // Use standard supabase query instead of RPC
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error("Error fetching subscription:", error);
      return createDefaultSubscription(session.user.id);
    }

    if (!data || data.length === 0) {
      return createDefaultSubscription(session.user.id);
    }

    // Prefer paid tier over free if multiple rows exist
    const tierPriority: Record<string, number> = { unlimited: 4, pro: 3, starter: 2, free: 1 };
    const best = data.sort((a, b) =>
      (tierPriority[b.tier] || 0) - (tierPriority[a.tier] || 0)
    )[0];

    return formatSubscription(best);
  } catch (error) {
    console.error("Error in getCurrentSubscription:", error);
    return null;
  }
};

// Create a default free subscription for the user
const createDefaultSubscription = async (userId: string): Promise<Subscription | null> => {
  try {
    // Check if ANY subscription already exists first
    const { data: existing } = await supabase
      .from('subscriptions')
      .select('id')
      .eq('user_id', userId)
      .limit(1);

    if (existing && existing.length > 0) {
      // Re-fetch instead of creating a duplicate
      const { data: refetched } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);
      return refetched?.[0] ? formatSubscription(refetched[0]) : null;
    }

    // Only create if truly no subscription exists
    const { data, error } = await supabase
      .from('subscriptions')
      .insert([{
        user_id: userId,
        tier: 'free',
        status: 'active'
      }])
      .select();

    if (error) {
      console.error("Error creating default subscription:", error);
      return null;
    }

    if (!data || data.length === 0) {
      return null;
    }

    return formatSubscription(data[0]);
  } catch (error) {
    console.error("Error in createDefaultSubscription:", error);
    return null;
  }
};

// Check subscription status
export const checkSubscriptionStatus = async (): Promise<void> => {
  try {
    const { data, error } = await supabase.functions.invoke('check-subscription');
    if (error) throw error;
    
    // Update local subscription if needed
    if (data.subscription) {
      // You could implement logic to update the local subscription state here
    }
  } catch (error) {
    console.error('Error checking subscription status:', error);
  }
};

// Helper to format subscription data
export const formatSubscription = (data: any): Subscription => {
  return {
    id: data.id,
    tier: data.tier || 'free',
    stripeSubscriptionId: data.stripe_subscription_id,
    stripeCustomerId: data.stripe_customer_id,
    revolutSubscriptionId: data.revolut_subscription_id,
    revolutCustomerId: data.revolut_customer_id,
    currentPeriodStart: data.current_period_start ? new Date(data.current_period_start) : undefined,
    currentPeriodEnd: data.current_period_end ? new Date(data.current_period_end) : undefined,
    status: data.status || 'active',
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  };
};
